import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, XIcon } from 'lucide-react';
import MISReportPDF from '../../components/MISChecklist/MISReportPDF';
import FLSSafetySystemsTable, { FLSSafetySystemRow } from '../../components/MISChecklist/FLSSafetySystemsTable';
import MEPFLSInfrastructureTable, { MEPFLSInfrastructureRow } from '../../components/MISChecklist/MEPFLSInfrastructureTable';
import FireIncidentDrillsTable, { FireIncidentDrillsRow } from '../../components/MISChecklist/FireIncidentDrillsTable';
import ERTSafetyEquipmentTable, { ERTSafetyEquipmentRow } from '../../components/MISChecklist/ERTSafetyEquipmentTable';
import IncidentSummaryTable, { IncidentSummaryRow } from '../../components/MISChecklist/IncidentSummaryTable';
import ExternalAuditReportTable from '../../components/KPIScoreCard/ExternalAuditReportTable';
import InternalAuditReportTable from '../../components/KPIScoreCard/InternalAuditReportTable';
import axiosInstance from '../../common/axiosInstance';
import { DotLoader } from '../../components/loader/Loader';
import { usePremisesStore } from '../../store/store';
import { Dropdown } from '../../components/dropdown/Dropdown';
import FileUpload from '../../components/FileUpload/FileUpload';
import { useDropzone } from 'react-dropzone';
import { Attachment } from '../../types/api';
import { toast } from 'react-toastify';
import { S3TempUrl } from "../../utils/s3Client";
import DocumentViewer from '../../components/DocumentViewer/DocumentViewer';
import PdfViewer from '../../components/pdf/PdfViewer';
import { useS3Upload } from "../../utils/s3Client";
import {
  calculateParameterScore,
  getInitialFireSafetyParameters,
  generateKPIScorecardName,
  getInitialExternalAuditRecords,
  getInitialInternalAuditRecords,
  AuditRecord,
} from '../../utils/syncMISToKPI';

// Utility functions for file handling
const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

const canPreviewFile = (filename: string): boolean => {
  const extension = getFileExtension(filename);
  return ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'ppt', 'pptx'].includes(extension);
};

interface FilePreviewState {
  url: string;
  name: string;
  type: string;
}

const MISChecklist: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id && id !== 'new';
  const isNewMode = id === 'new';
  const [isViewMode, setIsViewMode] = useState<boolean>(isEditMode ? true : false);

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [checklistName, setChecklistName] = useState<string>('');
  const [selectedPremises, setSelectedPremises] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [fireLicenseDate, setFireLicenseDate] = useState<string>('');
  const [fireNocIssueDate, setFireNocIssueDate] = useState<string>('');
  const [dateOfRenewal, setDateOfRenewal] = useState<string>('');
  const [statusAvailability, setStatusAvailability] = useState<string>('Yes');
  const [files, setFiles] = useState<File[] | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [maxFileSize, setMaxFileSize] = useState<number>(15);

  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();
  const [fileurl, setFileUrl] = useState<FilePreviewState>({
    url: '',
    name: '',
    type: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { getsignedurl } = S3TempUrl();
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 10,
    maxSize: 15 * 1024 * 1024, // 15MB
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc', '.docx'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls', '.xlsx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt', '.pptx'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    },
  });

  const handleFilesChange = (newFiles: File[] | null) => {
    if (newFiles) {
      setFiles(newFiles);
    }
  };

  const handleRemoveExistingAttachment = (index: number) => {
    if (isViewMode) return;

    setExistingAttachments((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  // Function to handle file preview
  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split('/').pop() ||
      '';

    if (canPreviewFile(filename)) {
      try {
        setIsLoading(true);

        const s3Key = attachment.url?.split('/').pop();
        if (!s3Key) {
          throw new Error('Invalid file URL');
        }

        const fileurl = await getsignedurl(s3Key, getFileExtension(filename));
        const filobj = {
          url: fileurl,
          name: filename,
          type: getFileExtension(filename),
        };
        setFileUrl(filobj);
        setIsDocumentViewerOpen(true);
      } catch (err: any) {
        console.error('Error previewing file:', err);
        setErrorMessage('File not found or failed to load');
        toast.error('Failed to preview file');
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.info('Preview not available for this file type. Use download instead.');
    }
  };

  const { allPremises, fetchPremises } = usePremisesStore();

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generates a checklist name from a date string (YYYY-MM-DD) → e.g. "MIS_Report_March_2026"
  const generateMISName = (dateStr: string): string => {
    if (!dateStr) {
      dateStr = getTodayDate();
    }
    try {
      const d = new Date(dateStr + 'T00:00:00'); // parse as local date
      const day = d.getDate();

      // Apply cutoff logic
      if (day <= 5) {
        d.setMonth(d.getMonth() - 1);
      }
      const mon = d.toLocaleString('en-US', { month: 'long' }); // "March"
      const year = d.getFullYear();                                     // 2026
      return `MIS_Report_${mon}_${year}`;
    } catch {
      return 'MIS_Report';
    }
  };

  // Initial template data for FLS Safety Systems
  const getInitialFLSSafetySystems = (): FLSSafetySystemRow[] => {
    const todayDate = getTodayDate();
    return [
      {
        srNo: 1,
        particulars: 'Fire Alarm System main panels inspected and found healthy',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 2,
        particulars: 'FAS repeater panels',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 3,
        particulars: 'Smoke and heat detectors',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 4,
        particulars: 'Gas detector',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 5,
        particulars: 'Manual call point',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 6,
        particulars: 'No. of extinguishers (including modular & gas suppression) system monthly inspected & AMC / PM',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 7,
        particulars: 'CO sensors',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 8,
        particulars: 'Sprinklers inspected in all floors and found healthy',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 9,
        particulars: 'Deluge system (if available)',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 10,
        particulars: 'Sprinkler valve open condition on each floor',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 11,
        particulars: 'Pressure gauges & Pressure switches Calibrated and in working condition.',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 12,
        particulars: 'Fire dampers & Fusible link type dampers',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 13,
        particulars: 'Automatic Rescue device in all lifts',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 14,
        particulars: 'NO. OF FIREMAN LIFT ADEQUATE & LIFTS CHECKED FOR ARD TALK BACK SYSTEM HEALTHINESS.(EACH BLOCK OR WING SHALL HAVE MINIMUM 1 NO. OF FIRE LIFT)',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 15,
        particulars: 'Fireman switch',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 16,
        particulars: 'Public addressing (PA) speakers',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 17,
        particulars: 'Gas Detectors',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 18,
        particulars: 'Fire Suppression system installed',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 19,
        particulars: 'Staircase pressurization fans For internal stair case or naturally ventilated or cross ventilated',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 20,
        particulars: "IS SMOKE EXTRACTION SYSTEM PROVIDED IN THE FLOOR CORRIDOR & BASEMENT WITH MINIMUM 12 AIR CHANGES PER HOUR AND SMOKE EXTRACTION INTERLOCKED WITH FIRE ALARM PANEL.",
        // 'Smoke extraction System in corridor and basement',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      // ── New parameters (sr.Nos 21–34 in Table I) ─────────────────────────
      {
        srNo: 21,
        particulars: 'ARE THE FIRE HYDRANT, SPRINKLER PUMP AND DIESEL ENGINE FIRE PUMP ARE ADEQUATELY DESIGNED & INSTALLED AS PER NBCS 2026 & LOCAL REGULATIONS',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 22,
        particulars: 'IS FIRE WATER UNDERGROUND TANK AND OVERHEAD TANK ARE OF ADEQUATE CAPACITY',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 23,
        particulars: 'IS PANEL MOUNT CLEAN AGENT SUPPRESSION SYSTEM OR FIXED GAS SUPPRESSION SYSTEM AVAILABLE IN THE ELECTRICAL PANEL ROOM AND UPS / BATTERY ROOM',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 24,
        particulars: 'ARE SPRINKLER SYSTEM PROVIDED IN ALL THE LOCATIONS AND ALSO IN CRITICAL AREAS LIKE ALL ICU, MRI AND OT OR ALTERNATIVELY MIST SYSTEM OR SUPPRESSION SYSTEM AVAILABLE',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 25,
        particulars: 'ARE SMOKE DETECTOR AND SPRINKLER SYSTEM PROVIDED ABOVE FALSE CEILING (IF GAP BETWEEN FALSE & TRUE CEILING EXCEEDS 800 MM) & ARE THEY INSPECTED',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 26,
        particulars: 'IS FIRE RATED DOOR PROVIDED FOR ALL ELECTRICAL PANEL ROOM, UPS ROOM, ICU, OT, MRI',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 27,
        particulars: 'IS FIRE RATED DOOR PROVIDED FOR ALL ICU, OT, MRI & WARDS WHERE NON AMBULATORY PATIENTS ARE AVAILABLE TO SELF PROTECT THEMSELVES',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 28,
        particulars: 'PROVISION OF LIFT LOBBY PRESSURIZATION (MINIMUM 25 PA), IF LIFT LOBBY FIRE SEGREGATION AND COMPARTMENTATION DONE THROUGH FIRE DOOR',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 29,
        particulars: 'IS LIFT LOBBY FIRE COMPARTMENTED IN FLOOR AREA & BASEMENT, IF NOT THEN IS LIFT WELL SHAFT PRESSURIZED WITH MINIMUM 50 PA',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 30,
        particulars: 'IS LIFT LOBBY COMPARTMENTED IN THE BASEMENT AREA AND PROVIDED WITH PRESSURIZATION OF MINIMUM 25 PA',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 31,
        particulars: 'ARE FIRE DAMPERS PROVIDED IN THE AHU DUCTS IN SUPPLY AND RETURN AIR SIDE',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 32,
        particulars: 'FIRE LICENSE — AVAILABILITY & VALIDITY',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 33,
        particulars: 'ARE RAMPS PROVIDED & WIDTH OF 2.4 METRES MAINTAINED',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 34,
        particulars: 'ARE CRITICAL LIFE SAVING EVACUATION EQUIPMENT LIKE SCBA, FIRE SUIT, STAIR CLIMBING WHEEL CHAIR, PATIENT STRETCHER AND EVACUATION MATTRESS AVAILABLE & MAINTAINED IN HEALTHY CONDITION',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
      {
        srNo: 35,
        particulars: 'ARE SPRINKLER SYSTEM PROVIDED IN ALL THE FLOORS AND FOUND ADEQUATE',
        installed: null,
        working: null,
        notWorking: null,
        lastInspectionDate: todayDate,
        remarks: null,
      },
    ];
  }

  // Initial template data for MEP-FLS Infrastructure
  const getInitialMEPFLSInfrastructure = (): MEPFLSInfrastructureRow[] => {
    return [
      {
        srNo: 36,
        systems: '(a) Fire alarm detection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Fire alarm panel shall have dual source of supply as redundancy (UPS) and internal batteries be efficient to provide standby back up for 48 hours and 20 minutes in continuous alarming mode.',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 37,
        systems: '(a) Fire alarm detection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Nomenclature / tagging available on all devices (Smoke Detectors, Heat Detectors, Manual call point and Flow Switch)',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 38,
        systems: '(a) Fire alarm detection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'All detectors cleaned once in 3 months ? Records to be maintained',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 39,
        systems: '(a) Fire alarm detection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'FAS Testing and maintenance records available of last 6 months?',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 40,
        systems: '(d) Emergency PA (Public Address) System',
        totalInstalledNos: null,
        workingNos: null,
        process: 'EPA system Inspected & integrated with FAS with minimum audibility of 40 to 50 db',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 41,
        systems: '(a) Fire Pump Testing, Maintenance & Water based fire protection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Fire pumps - Test & Maintenance (Daily, monthly, quarterly & annually) and B check for diesel engine',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 42,
        systems: '(a) Fire Pump Testing, Maintenance & Water based fire protection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Nomenclature marking on all fire pipelines with white paint available? (eg. by pass line, sprinkler hydrant, main header etc.).',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 43,
        systems: '(a) Fire Pump Testing, Maintenance & Water based fire protection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'All fire pumps kept in auto mode & tested daily. (5 min / day)',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 44,
        systems: '(b) No. of internal Hydrant, Hose reel inspection & external hydrant inspection along with flow test.',
        totalInstalledNos: null,
        workingNos: null,
        process: 'A pressure gauge should be installed on each floor for the internal hydrant, and one pressure gauge should be provided between every two external hydrants',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 45,
        systems: '(b) No. of internal Hydrant, Hose reel inspection & external hydrant inspection along with flow test.',
        totalInstalledNos: null,
        workingNos: null,
        process: 'All hydrant posts tested monthly & PPM done',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 46,
        systems: '(b) No. of internal Hydrant, Hose reel inspection & external hydrant inspection along with flow test.',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Fire hose boxes, hose pipe & branch provided with every fire hydrant post.',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 47,
        systems: '(c) Wet sprinkler system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Automatic sprinklers must be provisioned in all location at hospital (except server room, UPS room, Main electrical panel room)',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 48,
        systems: '(c) Wet sprinkler system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'All Sprinkler valves operating condition.',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 49,
        systems: '(c) Wet sprinkler system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Sprinkler bulbs checked for damage , clogging & colour loss in glass bulb sprinkler.',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 50,
        systems: 'Kitchen Hood suppression system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Kitchen hood are provided with Fire Suppression system ?',
        status: 'NA',
        completionDateOrRemark: null,
      },
      {
        srNo: 51,
        systems: 'Kitchen Hood suppression system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'All fire suppression system in healthy condition & inspected by OEM as per the advised inspection date ?',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 52,
        systems: 'Kitchen Hood suppression system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Fire suppression system integrated with auto gas shut off valve in all kitchens?',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 53,
        systems: 'Kitchen gas leakage detection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Main GAS Alarm panel must have direct power supply from main incomer with UPS/inverter back up of 60 minutes?',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 54,
        systems: 'Kitchen gas leakage detection system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Adequate numbers of Gas detectors installed in Gas bank & Kitchens?',
        status: null,
        completionDateOrRemark: '2',
      },
      {
        srNo: 55,
        systems: 'Fire suppression system',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Appropriate type of suppression system to be installed at Battery room, electrical panel room.',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 56,
        systems: 'Emergency lightings & Exit signs',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Emergency lights provided on escape route and tested, records maintained? with Min.90 min. battery back up.',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 57,
        systems: 'Emergency lightings & Exit signs',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Tagging to be done on all emergency lights.',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 58,
        systems: 'Emergency lightings & Exit signs',
        totalInstalledNos: null,
        workingNos: null,
        process: 'Self illuminated Exit signage\'s is available in hospitals',
        status: null,
        completionDateOrRemark: null,
      },
      {
        srNo: 59,
        systems: 'Compartmentation Once in 1800 m2, Availability & Inspection of Fire rated doors',
        totalInstalledNos: null,
        workingNos: null,
        process: 'All shafts at floor level & service openings to be closed with 2 hrs fire retardant materials.',
        status: null,
        completionDateOrRemark: null,
      },
    ];
  }

  const [flsSafetySystems, setFLSSafetySystems] = useState<FLSSafetySystemRow[]>(
    getInitialFLSSafetySystems()
  );

  const [mepFLSInfrastructure, setMEPFLSInfrastructure] = useState<MEPFLSInfrastructureRow[]>(
    getInitialMEPFLSInfrastructure()
  );

  // Initial template data for ERT Safety Equipment Status
  const getInitialERTSafetyEquipment = (): ERTSafetyEquipmentRow[] => [
    { srNo: 1, typeOfEquipment: 'Fire Proximity suit full set', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 2, typeOfEquipment: 'Self contained breathing apparatus (SCBA) Set & spare cylinder', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 3, typeOfEquipment: "Fire extinguisher's", responsibleDepartment: 'Security', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 4, typeOfEquipment: "Hose's", responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 5, typeOfEquipment: 'Type of Branches (eg : Short Branch pipe)', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 6, typeOfEquipment: 'FB5X Foam Making Branch Pipe or Mobile Foam unit with AR-SFFF (Alcohol-Resistant Synthetic Fluorine-Free Foam)', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 7, typeOfEquipment: 'First aid kit', responsibleDepartment: 'Housekeeping', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 8, typeOfEquipment: 'Evacuation chair', responsibleDepartment: 'Housekeeping', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 9, typeOfEquipment: 'Scoop Stretcher', responsibleDepartment: 'Housekeeping', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 10, typeOfEquipment: 'Wheel chair', responsibleDepartment: 'Housekeeping', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 11, typeOfEquipment: 'Fireman axe', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 12, typeOfEquipment: 'Sledge hammer', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 13, typeOfEquipment: 'Hammer', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 14, typeOfEquipment: 'Crow bar', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 15, typeOfEquipment: 'Shovel', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 16, typeOfEquipment: 'Fire Blankets', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 17, typeOfEquipment: 'Hydrant hose boxes', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 18, typeOfEquipment: 'Hose reels Drum', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 19, typeOfEquipment: 'Fire Bucket & stand', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 20, typeOfEquipment: 'Fire Escape mask', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 21, typeOfEquipment: 'Safety helmets', responsibleDepartment: 'Engineering & Housekeeping', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 22, typeOfEquipment: 'Florescent jockeys', responsibleDepartment: 'Engineering, Housekeeping & other department based on ERT', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 23, typeOfEquipment: 'Ear muff', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 24, typeOfEquipment: 'Safety Goggles', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 25, typeOfEquipment: 'Safety gloves', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 26, typeOfEquipment: 'Full body harness with double Lanyard', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 27, typeOfEquipment: 'Safety shoes', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 28, typeOfEquipment: 'Safety Gumboot', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 29, typeOfEquipment: 'Rope (Minimum 10mtrs)', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 30, typeOfEquipment: 'Search torch', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 31, typeOfEquipment: 'Mega phone', responsibleDepartment: 'Security', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 32, typeOfEquipment: 'Portable extraction machine with flexible pipe', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 33, typeOfEquipment: 'Elevator emergency rescue key', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 34, typeOfEquipment: 'Caution tape', responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
    { srNo: 35, typeOfEquipment: "Safety signage's for MEP", responsibleDepartment: 'Engineering', totalQuantity: null, condition: null, lastInspectionDate: null, remarks: null },
  ];

  // Initial template data for Fire Incident/Accidents, Drills & Training Programme
  const getInitialFireIncidentDrills = (): FireIncidentDrillsRow[] => [
    {
      srNo: 60,
      systems: 'NO. OF NEAR MISS INCIDENTS REPORTED',
      actualNo: null,
      targetNo: 3,
      process: null,
      status: null,
      completionDateOrRemark: null,
    },
    {
      srNo: 61,
      systems: 'NO. OF MAJOR ACCIDENTS REPORTED',
      actualNo: null,
      targetNo: 1,
      process: null,
      status: null,
      completionDateOrRemark: null,
    },
    {
      srNo: 62,
      systems: 'PERIODICAL TRAINING PROGRAMME ON FIRE SAFETY, FIRE EQUIPMENT USAGE AND MAINTENANCE. (ONCE IN 6 MONTHS) - 2 NOS / YEAR',
      actualNo: null,
      targetNo: 2,
      process: null,
      status: null,
      completionDateOrRemark: null,
    },
    {
      srNo: 63,
      systems: 'NO. OF INTERNAL FIRE DRILLS COMPLETED FOR MINIMUM 8 DIFFERENT SCENARIOS ONCE IN 6 MONTHS (FIRE IN ELECTRICAL / UPS PANEL ROOM, FIRE IN ICU, FIRE IN OT, FIRE IN HAZMAT AREA, FIRE IN DIESEL AREA / DG, FIRE IN RADIOLOGY, FIRE IN MGPS/OXYGEN AREA & FIRE IN MRI) - TOTAL 16 NOS. OF DRILLS',
      actualNo: null,
      targetNo: 16,
      process: null,
      status: null,
      completionDateOrRemark: null,
    },
    {
      srNo: 64,
      systems: 'NO.OF EXTERNAL GOVT FIRE & RESCUE DEPARTMENT FIRE DRILLS (ONCE IN A YEAR)',
      actualNo: null,
      targetNo: 1,
      process: null,
      status: null,
      completionDateOrRemark: null,
    },
  ];

  const [fireIncidentDrills, setFireIncidentDrills] = useState<FireIncidentDrillsRow[]>(
    getInitialFireIncidentDrills()
  );

  const [ertSafetyEquipment, setERTSafetyEquipment] = useState<ERTSafetyEquipmentRow[]>(
    getInitialERTSafetyEquipment()
  );

  const getInitialIncidentSummary = (): IncidentSummaryRow[] => [
    { description: 'Total Incidents', count: null, remarks: null },
    { description: 'Total Major Incidents', count: null, remarks: null },
    { description: 'No of Near Misses incident Reported', count: null, remarks: null },
    { description: 'No of Lost Time Injuries (LTI) case', count: null, remarks: null },
    { description: 'Fatalities', count: null, remarks: null },
  ];

  const [incidentSummary, setIncidentSummary] = useState<IncidentSummaryRow[]>(
    getInitialIncidentSummary()
  );

  const [externalAuditRecords, setExternalAuditRecords] = useState<AuditRecord[]>(
    getInitialExternalAuditRecords()
  );

  const [internalAuditRecords, setInternalAuditRecords] = useState<AuditRecord[]>(
    getInitialInternalAuditRecords()
  );

  // ── Auto-derive Incident Summary from Table III (fireIncidentDrills) ────────
  // Total Incidents    = Near Miss actualNo + Major Accidents actualNo
  // Total Major        = Major Accidents actualNo
  // Near Misses        = Near Miss actualNo
  useEffect(() => {
    const nearMissRow = fireIncidentDrills.find(r =>
      r.systems?.toUpperCase().includes('NEAR MISS')
    );
    const majorRow = fireIncidentDrills.find(r =>
      r.systems?.toUpperCase().includes('MAJOR ACCIDENT')
    );

    const nearMiss  = nearMissRow?.actualNo  ?? null;
    const major     = majorRow?.actualNo     ?? null;

    const totalIncidents =
      nearMiss !== null && major !== null
        ? nearMiss + major
        : nearMiss !== null
        ? nearMiss
        : major !== null
        ? major
        : null;

    setIncidentSummary(prev => [
      { ...prev[0], count: totalIncidents },
      { ...prev[1], count: major },
      { ...prev[2], count: nearMiss },
      { ...prev[3], count: prev[3].count },   // LTI — not derivable, keep existing
      { ...prev[4], count: prev[4].count },   // Fatalities — not derivable, keep existing
    ]);
  }, [fireIncidentDrills]);

  // Fetch premises on mount
  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  // Auto-fill date + name when creating a new checklist
  useEffect(() => {
    if (isNewMode && !selectedDate) {
      const todayStr = getTodayDate();
      setSelectedDate(todayStr);
      setChecklistName(generateMISName(todayStr));
    }
  }, [isNewMode, selectedDate]);

  // When date changes on a new checklist, always keep the name in sync
  useEffect(() => {
    if (isNewMode && selectedDate) {
      setChecklistName(generateMISName(selectedDate));
    }
  }, [selectedDate, isNewMode]);

  // Load checklist data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadChecklist(id);
    } else if (isNewMode) {
      // Reset to initial values for new checklist
      setFLSSafetySystems(getInitialFLSSafetySystems());
      setMEPFLSInfrastructure(getInitialMEPFLSInfrastructure());
      setFireIncidentDrills(getInitialFireIncidentDrills());
      setERTSafetyEquipment(getInitialERTSafetyEquipment());
      setIncidentSummary(getInitialIncidentSummary());
      setExternalAuditRecords(getInitialExternalAuditRecords());
      setInternalAuditRecords(getInitialInternalAuditRecords());
      const todayStr = getTodayDate();
      setChecklistName(generateMISName(todayStr));
      setSelectedPremises('');
      setFiles(null);
      setExistingAttachments([]);
    }
  }, [id, isEditMode, isNewMode]);

  const loadChecklist = async (checklistId: string) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/fire-safety-mis-checklists/${checklistId}`);
      const data = response.data?.data || response.data;

      if (data) {
        setChecklistName(data.name || '');
        setSelectedPremises(data.premisesId || '');
        setSelectedDate(data.date || '');
        setFireLicenseDate(data.fireLicenseDate || '');
        setFireNocIssueDate(data.fireNocIssueDate || '');
        setDateOfRenewal(data.dateOfRenewal || '');
        setStatusAvailability(data.statusAvailability || 'Yes');

        if (data.fireAndLifeSafetyEquipment && Array.isArray(data.fireAndLifeSafetyEquipment)) {
          setFLSSafetySystems(data.fireAndLifeSafetyEquipment);
        } else if (data.flsSafetySystems && Array.isArray(data.flsSafetySystems)) {
          setFLSSafetySystems(data.flsSafetySystems);
        }

        if (data.fireAndLifeSafetySystem && Array.isArray(data.fireAndLifeSafetySystem)) {
          setMEPFLSInfrastructure(data.fireAndLifeSafetySystem);
        } else if (data.mepFLSInfrastructure && Array.isArray(data.mepFLSInfrastructure)) {
          setMEPFLSInfrastructure(data.mepFLSInfrastructure);
        }

        if (data.fireIncidentDrills && Array.isArray(data.fireIncidentDrills)) {
          setFireIncidentDrills(data.fireIncidentDrills);
        }

        if (data.ertSafetyEquipment && Array.isArray(data.ertSafetyEquipment)) {
          setERTSafetyEquipment(data.ertSafetyEquipment);
        }

        if (data.incidentSummary && Array.isArray(data.incidentSummary)) {
          setIncidentSummary(data.incidentSummary);
        }

        if (data.externalAuditRecords && Array.isArray(data.externalAuditRecords)) {
          setExternalAuditRecords(data.externalAuditRecords);
        }

        if (data.internalAuditRecords && Array.isArray(data.internalAuditRecords)) {
          setInternalAuditRecords(data.internalAuditRecords);
        }

        if (data.attachments && Array.isArray(data.attachments)) {
          setExistingAttachments(data.attachments);
        }
      }
    } catch (error: any) {
      console.error('Error loading checklist:', error);
      alert(error.response?.data?.message || 'Failed to load checklist');
      navigate('/safety-equipment/fire-safety-mis-checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!checklistName.trim()) {
      alert('Please enter a checklist name');
      return;
    }

    if (!selectedPremises) {
      alert('Please select a premises');
      return;
    }

    if (!selectedDate) {
      alert('Please select a date');
      return;
    }

    if (!statusAvailability) {
      alert('Please select status availability');
      return;
    }

    if (!fireNocIssueDate) {
      alert('Please select fire noc issue date');
      return;
    }

    for (let i = 0; i < flsSafetySystems.length; i++) {
      const row = flsSafetySystems[i];
      if (!row.remarks?.trim()) {
        alert(`Remarks is mandatory for all items in Fire & Life Safety Equipment `);
        return;
      }
    }

    for (let i = 0; i < mepFLSInfrastructure.length; i++) {
      const row = mepFLSInfrastructure[i];
      if (!row.completionDateOrRemark?.trim()) {
        alert(`Remarks is mandatory for all items in Fire & Life Safety System `);
        return;
      }
    }

    for (let i = 0; i < fireIncidentDrills.length; i++) {
      const row = fireIncidentDrills[i];
      if (!row.completionDateOrRemark?.trim()) {
        alert(`Remarks is mandatory for all items in Fire Incident/Drills `);
        return;
      }
    }

    for (let i = 0; i < ertSafetyEquipment.length; i++) {
      const row = ertSafetyEquipment[i];
      if (!row.remarks?.trim()) {
        alert(`Remarks is mandatory for all items in ERT Safety Equipment `);
        return;
      }
    }

    // Validate remarks for items that require them
    const missingRemarks: string[] = [];

    // FLS Safety Systems: remarks mandatory when notWorking > 0
    flsSafetySystems.forEach((row) => {
      if (row.notWorking !== null && row.notWorking !== undefined && row.notWorking > 0 && !row.remarks?.trim()) {
        missingRemarks.push(`Fire & Life Safety Equipment → Sr.No ${row.srNo}: "${row.particulars.substring(0, 50)}..."`);
      }
    });

    // MEP-FLS Infrastructure: remarks mandatory when status is "no" or "partially"
    mepFLSInfrastructure.forEach((row) => {
      const statusLower = (row.status || '').toLowerCase();
      if ((statusLower === 'no' || statusLower === 'partially') && !row.completionDateOrRemark?.trim()) {
        missingRemarks.push(`Fire & Life Safety System → Sr.No ${row.srNo}: "${row.process.substring(0, 50)}..."`);
      }
    });

    // Fire Incident Drills: remarks mandatory when status is "no" or "pending"
    fireIncidentDrills.forEach((row) => {
      const statusLower = (row.status || '').toLowerCase();
      if ((statusLower === 'no' || statusLower === 'pending') && !row.completionDateOrRemark?.trim()) {
        missingRemarks.push(`Fire Incident/Drills → Sr.No ${row.srNo}: "${row.systems.substring(0, 50)}..."`);
      }
    });

    if (missingRemarks.length > 0) {
      alert(
        `Remarks are mandatory for non-compliant / not working items.\n\nPlease add remarks for:\n${missingRemarks.slice(0, 5).join('\n')}${missingRemarks.length > 5 ? `\n...and ${missingRemarks.length - 5} more` : ''}`
      );
      return;
    }

    try {
      setSaving(true);
      // Get premises name
      const selectedPremise = allPremises.find((p: any) => p._id === selectedPremises);
      const premisesName = selectedPremise?.premisesName || '';

      // Upload new files to S3
      let uploadedAttachments: Attachment[] = [...existingAttachments];

      if (files && files.length > 0) {
        try {
          const uploadPromises = files.map(async (file) => {
            const url = await uploadFileToS3(file);
            return {
              url,
              filename: file.name,
              name: file.name,
              originalName: file.name,
              uploadedAt: new Date().toISOString(),
            };
          });

          const newAttachments = await Promise.all(uploadPromises);
          uploadedAttachments = [...uploadedAttachments, ...newAttachments];
        } catch (uploadError: any) {
          console.error('Error uploading files:', uploadError);
          alert('Failed to upload some files. Please try again.');
          setSaving(false);
          return;
        }
      }

      const checklistData = {
        name: checklistName.trim(),
        premisesId: selectedPremises || null,
        premisesName: premisesName,
        date: selectedDate || null,
        fireAndLifeSafetyEquipment: flsSafetySystems,
        fireAndLifeSafetySystem: mepFLSInfrastructure,
        fireIncidentDrills,
        ertSafetyEquipment,
        incidentSummary,
        externalAuditRecords,
        internalAuditRecords,
        attachments: uploadedAttachments,
        fireLicenseDate,
        fireNocIssueDate,
        dateOfRenewal,
        statusAvailability,
      };

      let savedChecklist = null;
      if (isEditMode && id) {
        // Update existing
        const response = await axiosInstance.put(`/api/fire-safety-mis-checklists/${id}`, checklistData);
        alert('Checklist updated successfully!');
        savedChecklist = response.data?.data || checklistData;
      } else {
        // Create new
        const response = await axiosInstance.post('/api/fire-safety-mis-checklists', checklistData);
        alert('Checklist created successfully!');
        savedChecklist = response.data?.data || checklistData;
      }

      // Auto-sync to KPI Scorecard
      if (selectedPremises && savedChecklist) {
        try {
          await syncToKPIScorecard(savedChecklist, premisesName);
        } catch (syncError: any) {
          console.error('Error syncing to KPI Scorecard:', syncError);
          // Don't block navigation if sync fails, just log it
          toast.warning('MIS Checklist saved, but auto-sync to KPI Scorecard failed. You can sync manually later.');
        }
      }

      navigate('/safety-equipment/fire-safety-mis-checklist');
    } catch (error: any) {
      console.error('Error saving checklist:', error);
      alert(error.response?.data?.message || 'Failed to save checklist');
    } finally {
      setSaving(false);
    }
  };

  const handleFLSSafetySystemChange = useCallback(
    (index: number, updatedRow: FLSSafetySystemRow) => {
      setFLSSafetySystems((prev) => {
        // Check for duplicate srNo (should not happen since srNo is not editable, but just in case)
        const duplicateIndex = prev.findIndex((row, idx) => idx !== index && row.srNo === updatedRow.srNo);
        if (duplicateIndex !== -1) {
          alert(`Sr.No ${updatedRow.srNo} is already used. Each row must have a unique Sr.No.`);
          return prev;
        }
        const newData = [...prev];
        newData[index] = updatedRow;
        return newData;
      });
    },
    []
  );

  const handleMEPFLSInfrastructureChange = useCallback(
    (index: number, updatedRow: MEPFLSInfrastructureRow) => {
      setMEPFLSInfrastructure((prev) => {
        // Check for duplicate srNo (should not happen since srNo is not editable, but just in case)
        const duplicateIndex = prev.findIndex((row, idx) => idx !== index && row.srNo === updatedRow.srNo);
        if (duplicateIndex !== -1) {
          alert(`Sr.No ${updatedRow.srNo} is already used. Each row must have a unique Sr.No.`);
          return prev;
        }

        const newData = [...prev];
        newData[index] = updatedRow;
        return newData;
      });
    },
    []
  );

  const handleFireIncidentDrillsChange = useCallback(
    (index: number, updatedRow: FireIncidentDrillsRow) => {
      setFireIncidentDrills((prev) => {
        // Check for duplicate srNo (should not happen since srNo is not editable, but just in case)
        const duplicateIndex = prev.findIndex((row, idx) => idx !== index && row.srNo === updatedRow.srNo);
        if (duplicateIndex !== -1) {
          alert(`Sr.No ${updatedRow.srNo} is already used. Each row must have a unique Sr.No.`);
          return prev;
        }

        const newData = [...prev];
        newData[index] = updatedRow;
        return newData;
      });
    },
    []
  );

  const handleERTSafetyEquipmentChange = useCallback(
    (index: number, updatedRow: ERTSafetyEquipmentRow) => {
      setERTSafetyEquipment((prev) => {
        const newData = [...prev];
        newData[index] = updatedRow;
        return newData;
      });
    },
    []
  );

  const handleIncidentSummaryChange = useCallback(
    (index: number, updatedRow: IncidentSummaryRow) => {
      setIncidentSummary((prev) => {
        const newData = [...prev];
        newData[index] = updatedRow;
        return newData;
      });
    },
    []
  );

  const handleExternalAuditChange = useCallback(
    (index: number, updatedRecord: AuditRecord) => {
      setExternalAuditRecords((prev) => {
        const newData = [...prev];
        newData[index] = updatedRecord;
        return newData;
      });
    },
    []
  );

  const handleInternalAuditChange = useCallback(
    (index: number, updatedRecord: AuditRecord) => {
      setInternalAuditRecords((prev) => {
        const newData = [...prev];
        newData[index] = updatedRecord;
        return newData;
      });
    },
    []
  );

  // Function to sync MIS Checklist data to KPI Scorecard
  const syncToKPIScorecard = async (checklistData: any, premisesName: string) => {
    try {
      const { premisesId, date, fireAndLifeSafetyEquipment, fireAndLifeSafetySystem, fireIncidentDrills, incidentSummary: summary } = checklistData;

      if (!premisesId) {
        console.warn('No premises ID, skipping KPI Scorecard sync');
        return;
      }

      // ── Derive incident counts directly from incidentSummary (preferred) ──
      // incidentSummary rows: [0]=Total, [1]=Major, [2]=NearMiss, [3]=LTI, [4]=Fatalities
      const summaryArr: any[] = Array.isArray(summary) ? summary : [];
      const nearMissRow = summaryArr.find((r: any) =>
        (r.description || '').toUpperCase().includes('NEAR MISS')
      );
      const majorRow = summaryArr.find((r: any) =>
        (r.description || '').toUpperCase().includes('MAJOR')
      );
      const nearMissActual: number | null = nearMissRow?.count ?? null;
      const majorActual:    number | null = majorRow?.count    ?? null;

      // Fall back to fireIncidentDrills if incidentSummary has no data yet
      const drills: any[] = Array.isArray(fireIncidentDrills) ? fireIncidentDrills : [];
      const nearMissFromDrills = drills.find((r: any) =>
        r.srNo === 60 || (r.systems || '').toUpperCase().includes('NEAR MISS')
      );
      const majorFromDrills = drills.find((r: any) =>
        r.srNo === 61 || (r.systems || '').toUpperCase().includes('MAJOR ACCIDENT')
      );
      const nearMissTarget: number = nearMissFromDrills?.targetNo ?? 0;
      const majorTarget:    number = majorFromDrills?.targetNo    ?? 0;
      const resolvedNearMiss: number | null = nearMissActual ?? nearMissFromDrills?.actualNo ?? null;
      const resolvedMajor:    number | null = majorActual    ?? majorFromDrills?.actualNo    ?? null;
      const nearMissPct: number | null = (nearMissTarget > 0 && resolvedNearMiss != null)
        ? (resolvedNearMiss / nearMissTarget) * 100 : null;
      const majorPct: number | null = (majorTarget > 0 && resolvedMajor != null)
        ? (resolvedMajor / majorTarget) * 100 : null;

      // Check if KPI Scorecard already exists for this date and premises
      let existingScorecard = null;
      if (date) {
        try {
          const existingScorecardsResponse = await axiosInstance.get('/api/fire-safety-scorecards', {
            params: { premisesId, date },
          });
          const existingScorecards = existingScorecardsResponse.data?.data || existingScorecardsResponse.data || [];
          if (Array.isArray(existingScorecards) && existingScorecards.length > 0) {
            existingScorecard = existingScorecards.find(
              (sc: any) => sc.date === date && sc.premisesId === premisesId
            );
          }
        } catch (err) {
          console.warn('Error checking for existing scorecard:', err);
        }
      }

      // Calculate scores for all parameters
      const initialParameters = getInitialFireSafetyParameters();
      const calculatedParameters = initialParameters.map((param) =>
        calculateParameterScore(
          param,
          fireAndLifeSafetyEquipment || [],
          fireAndLifeSafetySystem || [],
          fireIncidentDrills || []
        )
      );

      const scorecardName = generateKPIScorecardName(date);
      const scorecardData = {
        name: scorecardName,
        description: `Auto-generated from MIS Checklist: ${checklistData.name || 'N/A'}`,
        premisesId,
        premisesName,
        date: date || null,
        fireSafetyParameters: calculatedParameters,
        externalAuditRecords: checklistData.externalAuditRecords || getInitialExternalAuditRecords(),
        internalAuditRecords: checklistData.internalAuditRecords || getInitialInternalAuditRecords(),
        // ── Incident data saved directly — no need to re-derive from sr.Nos ──
        nearMissActual:  resolvedNearMiss,
        nearMissPct,
        majorActual:     resolvedMajor,
        majorPct,
      };

      if (existingScorecard && existingScorecard._id) {
        await axiosInstance.put(`/api/fire-safety-scorecards/${existingScorecard._id}`, scorecardData);
        toast.success(`KPI Scorecard updated automatically for ${date || 'this date'}`);
      } else {
        await axiosInstance.post('/api/fire-safety-scorecards', scorecardData);
        toast.success(`KPI Scorecard created automatically for ${date || 'this date'}`);
      }
    } catch (error: any) {
      console.error('Error syncing to KPI Scorecard:', error);
      if (error.response?.status === 409 && error.response?.data?.error?.includes('already exists')) {
        try {
          const findResponse = await axiosInstance.get('/api/fire-safety-scorecards', {
            params: { premisesId: checklistData.premisesId, date: checklistData.date },
          });
          const scorecards = findResponse.data?.data || findResponse.data || [];
          if (Array.isArray(scorecards) && scorecards.length > 0) {
            const existing = scorecards.find(
              (sc: any) => sc.date === checklistData.date && sc.premisesId === checklistData.premisesId
            );
            if (existing && existing._id) {
              const drills2: any[] = Array.isArray(checklistData.fireIncidentDrills) ? checklistData.fireIncidentDrills : [];
              const summary2: any[] = Array.isArray(checklistData.incidentSummary) ? checklistData.incidentSummary : [];
              const nmRow2 = summary2.find((r: any) => (r.description || '').toUpperCase().includes('NEAR MISS'));
              const mjRow2 = summary2.find((r: any) => (r.description || '').toUpperCase().includes('MAJOR'));
              const nmDrill2 = drills2.find((r: any) => r.srNo === 60 || r.srNo === 45 || (r.systems || '').toUpperCase().includes('NEAR MISS'));
              const mjDrill2 = drills2.find((r: any) => r.srNo === 61 || r.srNo === 46 || (r.systems || '').toUpperCase().includes('MAJOR ACCIDENT'));
              const nmActual2: number | null = nmRow2?.count ?? nmDrill2?.actualNo ?? null;
              const mjActual2: number | null = mjRow2?.count ?? mjDrill2?.actualNo ?? null;
              const nmTarget2: number = nmDrill2?.targetNo ?? 0;
              const mjTarget2: number = mjDrill2?.targetNo ?? 0;

              const initialParameters = getInitialFireSafetyParameters();
              const calculatedParameters = initialParameters.map((param) =>
                calculateParameterScore(
                  param,
                  checklistData.fireAndLifeSafetyEquipment || [],
                  checklistData.fireAndLifeSafetySystem || [],
                  checklistData.fireIncidentDrills || []
                )
              );
              const retryScorecardData = {
                name: generateKPIScorecardName(checklistData.date),
                description: `Auto-generated from MIS Checklist: ${checklistData.name || 'N/A'}`,
                premisesId: checklistData.premisesId,
                premisesName,
                date: checklistData.date || null,
                fireSafetyParameters: calculatedParameters,
                externalAuditRecords: checklistData.externalAuditRecords || getInitialExternalAuditRecords(),
                internalAuditRecords: checklistData.internalAuditRecords || getInitialInternalAuditRecords(),
                nearMissActual: nmActual2,
                nearMissPct: (nmTarget2 > 0 && nmActual2 != null) ? (nmActual2 / nmTarget2) * 100 : null,
                majorActual: mjActual2,
                majorPct: (mjTarget2 > 0 && mjActual2 != null) ? (mjActual2 / mjTarget2) * 100 : null,
              };
              await axiosInstance.put(`/api/fire-safety-scorecards/${existing._id}`, retryScorecardData);
              toast.success(`KPI Scorecard updated automatically for ${checklistData.date || 'this date'}`);
              return;
            }
          }
        } catch (retryError) {
          console.error('Error in retry logic:', retryError);
        }
      }
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <DotLoader />
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-4">
      <div className="w-full mt-20 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {isEditMode ? 'Edit' : isNewMode ? 'Create New' : ''} Fire Safety MIS Checklist
            </h2>
            <p className="text-gray-600">
              {isNewMode
                ? 'Fill in the fields below and save to create a new checklist'
                : 'View and manage FLS Safety Systems and MEP-FLS Infrastructure status'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/safety-equipment/mis-checklist')}
              className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to List
            </button>
            {isEditMode && (
              <MISReportPDF
                checklistName={checklistName}
                premisesName={
                  allPremises.find((p: any) => p._id === selectedPremises)?.premisesName || ''
                }
                date={selectedDate}
                flsSafetySystems={flsSafetySystems}
                mepFLSInfrastructure={mepFLSInfrastructure}
                fireIncidentDrills={fireIncidentDrills}
                ertSafetyEquipment={ertSafetyEquipment}
                incidentSummary={incidentSummary}
                fireLicenseDate={fireLicenseDate}
                fireNocIssueDate={fireNocIssueDate}
                dateOfRenewal={dateOfRenewal}
                statusAvailability={statusAvailability}
                externalAuditRecords={externalAuditRecords}
                internalAuditRecords={internalAuditRecords}
              />
            )}
            {isEditMode && (
              <button
                onClick={() => setIsViewMode(!isViewMode)}
                className="flex items-center gap-2 py-2 px-4 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                {isViewMode ? (
                  <>
                    <Eye className="w-5 h-5" />
                    Edit Mode
                  </>
                ) : (
                  <>
                    <EyeOff className="w-5 h-5" />
                    View Mode
                  </>
                )}
              </button>
            )}
            {(isEditMode || isNewMode) && !isViewMode && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {isEditMode ? 'Update' : 'Save'} Checklist
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {(isEditMode || isNewMode) && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Checklist Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={checklistName}
                readOnly
                disabled
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed select-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Premises <span className="text-red-500">*</span>
              </label>
              <div className={isViewMode ? 'pointer-events-none opacity-60' : ''}>
                <Dropdown
                  selectedValue={selectedPremises}
                  onChange={setSelectedPremises}
                  type="premises"
                />
              </div>
              {!isViewMode && !selectedPremises && (
                <p className="text-xs text-amber-600 mt-1">⚠ A specific premises must be selected</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={isViewMode}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
              />
            </div>
          </div>
        </div>
      )}

      {(isEditMode || isNewMode) && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <FileUpload
            mode={isViewMode ? 'view' : isEditMode ? 'update' : 'create'}
            files={files}
            onFilesChange={handleFilesChange}
            existingAttachments={existingAttachments}
            onRemoveExistingAttachment={handleRemoveExistingAttachment}
            onPreviewFile={handlePreviewFile}
            uploading={uploading}
            uploadProgress={uploadProgress}
            disabled={isDisabled || isViewMode}
            maxFileSizeMB={maxFileSize}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
          />
        </div>
      )}

      {/* Fire License Status Info */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Fire License</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fire License Issue Date</label>
            <input
              type="date"
              value={fireLicenseDate}
              onChange={(e) => setFireLicenseDate(e.target.value)}
              disabled={isViewMode}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fire NOC Issue Date</label>
            <input
              type="date"
              value={fireNocIssueDate}
              onChange={(e) => setFireNocIssueDate(e.target.value)}
              disabled={isViewMode}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Fire License Renewal</label>
            <input
              type="date"
              value={dateOfRenewal}
              onChange={(e) => setDateOfRenewal(e.target.value)}
              disabled={isViewMode}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''} ${dateOfRenewal && new Date(dateOfRenewal) < new Date() ? 'border-red-500 text-red-600' : 'border-gray-300'}`}
            />
            {dateOfRenewal && new Date(dateOfRenewal) < new Date() && (
              <p className="text-red-500 text-xs mt-1">Crossed Date of Renewal</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status Validity</label>
            <select
              value={statusAvailability}
              onChange={(e) => setStatusAvailability(e.target.value)}
              disabled={isViewMode}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''} ${statusAvailability === 'No' ? 'border-red-500 text-red-600 font-bold' : 'border-gray-300'}`}
            >
              <option value="valid">Valid</option>
              <option value="invalid">Invalid</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {/* External Audit Report Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">External Audit Report</h3>
          <p className="text-sm text-gray-500 mb-2">Fill in the audit NC data below. This will be reflected in the Fire Safety Score Card (read-only).</p>
          <ExternalAuditReportTable
            records={externalAuditRecords}
            onChange={handleExternalAuditChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
          />
        </div>

        {/* Internal Audit Report Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Internal Audit Report</h3>
          <p className="text-sm text-gray-500 mb-2">Fill in the audit NC data below. This will be reflected in the Fire Safety Score Card (read-only).</p>
          <InternalAuditReportTable
            records={internalAuditRecords}
            onChange={handleInternalAuditChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
          />
        </div>

        {/* Fire and Life safety Equipment Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <FLSSafetySystemsTable
            data={flsSafetySystems}
            onChange={handleFLSSafetySystemChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
          />
        </div>

        {/* Fire and Life safety system Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <MEPFLSInfrastructureTable
            data={mepFLSInfrastructure}
            onChange={handleMEPFLSInfrastructureChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
          />
        </div>

        {/* Fire Incident/Accidents, Drills & Training Programme Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <FireIncidentDrillsTable
            data={fireIncidentDrills}
            onChange={handleFireIncidentDrillsChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
          />
        </div>

        {/* ERT Safety Equipment Status Table */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <ERTSafetyEquipmentTable
            data={ertSafetyEquipment}
            onChange={handleERTSafetyEquipmentChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
          />
        </div>

        {/* Incident Summary Table */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <IncidentSummaryTable
            data={incidentSummary}
            onChange={handleIncidentSummaryChange}
            isEditable={(isEditMode || isNewMode) && !isViewMode}
            readOnlyCountIndices={[0, 1, 2]}
          />
        </div>
      </div>

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">
                Document Preview: {fileurl.name}
              </h3>
              <button
                onClick={() => {
                  setIsDocumentViewerOpen(false);
                  setFileUrl({ url: '', name: '', type: '' });
                  setErrorMessage('');
                }}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Close document viewer"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {errorMessage}
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : fileurl.url ? (
                fileurl.type === 'pdf' ? (
                  <div className="w-full h-full">
                    <PdfViewer fileUrl={fileurl.url} />
                  </div>
                ) : (
                  <div className="w-full h-full">
                    <DocumentViewer file={fileurl.url} name={fileurl.name} />
                  </div>
                )
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p>No document to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MISChecklist;
